/**
 * Reading two instants against each other.
 *
 * In `lib/` because both the flight board and the international arrivals journal need it, and a
 * feature may not import from another feature. `dayOffset` was already written
 * twice — once in SchedulePage and once in FlightPanel — which is what a helper
 * living in a feature turns into the moment a second screen wants it.
 *
 * Everything here works on LOCAL dates, not on elapsed milliseconds. A board is
 * read in the operation's own wall clock: "the next day" means the date changed,
 * not that 24 hours passed.
 */

/**
 * A `Date` as a `datetime-local` input value: `2026-08-31T02:34`.
 *
 * Read through the LOCAL components, never `toISOString()`. That converts to UTC
 * first, so a wall clock five hours east comes out on the previous day — the same
 * trap `lib/api/date.ts` documents for the request direction. Subtracting
 * `getTimezoneOffset()` before calling it produces the right string most of the
 * year and the wrong one across a DST boundary; reading the components is simply
 * correct.
 */
export const toLocalInput = (date: Date): string => {
  const pad = (part: number) => String(part).padStart(2, '0');
  const day = `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  return `${day}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * The two halves of a `datetime-local` value: `2026-09-02T14:20`.
 *
 * Read apart because the range filters are a date box and a time box rather than
 * one combined field, and the value they add up to is still a single instant. See
 * components/ui/DateTimeInput for why they are split at all.
 *
 * Both return '' for an unset or malformed value, which is the same thing an empty
 * input holds — so a caller never has to tell "no date" from "no value".
 */
export const dateOfInput = (value: string): string => value.split('T')[0] ?? '';

export const timeOfInput = (value: string): string => value.split('T')[1] ?? '';

/** `21.08` — the year is on the filter above, and the full stamp is on hover. */
export const dayMonth = (value: string): string => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}`;
};

/** Whether two instants fall on the same local date. */
export const sameDay = (left: string | number, right: string | number): boolean => {
  const a = new Date(left);
  const b = new Date(right);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return false;

  return a.toDateString() === b.toDateString();
};

/**
 * Whole calendar days between two instants, by local DATE rather than by elapsed
 * time.
 *
 * `23:50` against `00:10` is one day apart even though forty minutes passed, and
 * that is the number a timetable prints as `+1`.
 */
export const dayOffset = (reference: string, value: string): number => {
  const from = new Date(reference);
  const to = new Date(value);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 0;

  const startOfFrom = new Date(from.getFullYear(), from.getMonth(), from.getDate());
  const startOfTo = new Date(to.getFullYear(), to.getMonth(), to.getDate());

  return Math.round((startOfTo.getTime() - startOfFrom.getTime()) / 86_400_000);
};

export const minutesBetween = (reference: string, value: string): number => {
  const from = new Date(reference);
  const to = new Date(value);
  if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return 0;

  return Math.round((to.getTime() - from.getTime()) / 60_000);
};

/**
 * `+54`, or `+1:20` once it passes an hour.
 *
 * Minutes are how a delay is quoted, right up to the point where the number stops
 * being readable: nobody hears "eighty minutes late" and pictures the same thing as
 * "an hour and twenty".
 */
export const formatDrift = (minutes: number): string => {
  const sign = minutes > 0 ? '+' : '−';
  const total = Math.abs(minutes);
  if (total < 60) return `${sign}${total}`;

  return `${sign}${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};
